import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import { Textarea } from './ui/textarea';
import { useToast } from '../hooks/use-toast';
import PageHeader from './ui/PageHeader';
import {
  MessageSquare, Mic, Volume2, Camera, Globe, Users, Send,
  AlertTriangle, Upload, Download, Settings, Search, Plus,
  Play, Pause, Image as ImageIcon, FileText, ChevronRight,
  Languages, Zap, Shield, Clock, Check, X, Loader2, Link, Unlink
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Top 10 construction industry languages
const LANGUAGES = [
  { code: 'en', name: 'English', flag: '🇺🇸' },
  { code: 'es', name: 'Spanish', flag: '🇪🇸' },
  { code: 'zh', name: 'Mandarin Chinese', flag: '🇨🇳' },
  { code: 'pt', name: 'Portuguese', flag: '🇧🇷' },
  { code: 'fr', name: 'French', flag: '🇫🇷' },
  { code: 'ar', name: 'Arabic', flag: '🇸🇦' },
  { code: 'hi', name: 'Hindi', flag: '🇮🇳' },
  { code: 'vi', name: 'Vietnamese', flag: '🇻🇳' },
  { code: 'ko', name: 'Korean', flag: '🇰🇷' },
  { code: 'pl', name: 'Polish', flag: '🇵🇱' }
];

// Pre-translated safety phrases
const SAFETY_PHRASES = {
  emergency: {
    en: 'Emergency stop - danger!',
    es: '¡Paro de emergencia - peligro!',
    zh: '紧急停止 - 危险！',
    pt: 'Parada de emergência - perigo!',
    fr: 'Arrêt d\'urgence - danger!',
    ar: 'توقف طارئ - خطر!',
    hi: 'आपातकालीन रोक - खतरा!',
    vi: 'Dừng khẩn cấp - nguy hiểm!',
    ko: '비상 정지 - 위험!',
    pl: 'Zatrzymanie awaryjne - niebezpieczeństwo!'
  },
  medical: {
    en: 'Medical emergency - call for help!',
    es: '¡Emergencia médica - pide ayuda!',
    zh: '医疗紧急情况 - 呼叫帮助！',
    pt: 'Emergência médica - peça ajuda!',
    fr: 'Urgence médicale - appelez à l\'aide!',
    ar: 'حالة طبية طارئة - اطلب المساعدة!',
    hi: 'चिकित्सा आपातकाल - मदद के लिए कॉल करें!',
    vi: 'Cấp cứu y tế - gọi cứu trợ!',
    ko: '의료 응급 상황 - 도움을 요청하세요!',
    pl: 'Nagły wypadek medyczny - wezwij pomoc!'
  },
  evacuate: {
    en: 'Evacuate immediately!',
    es: '¡Evacuar inmediatamente!',
    zh: '立即疏散！',
    pt: 'Evacuar imediatamente!',
    fr: 'Évacuez immédiatement!',
    ar: 'إخلاء فوري!',
    hi: 'तुरंत खाली करें!',
    vi: 'Di tản ngay lập tức!',
    ko: '즉시 대피하십시오!',
    pl: 'Natychmiast ewakuować!'
  },
  ppe_required: {
    en: 'PPE required in this area',
    es: 'EPP requerido en esta área',
    zh: '此区域需要个人防护装备',
    pt: 'EPI obrigatório nesta área',
    fr: 'EPI requis dans cette zone',
    ar: 'معدات الحماية الشخصية مطلوبة في هذه المنطقة',
    hi: 'इस क्षेत्र में पीपीई आवश्यक है',
    vi: 'Cần thiết bị bảo hộ ở khu vực này',
    ko: '이 구역에는 개인 보호 장비가 필요합니다',
    pl: 'Wymagane środki ochrony osobistej w tym obszarze'
  },
  danger: {
    en: 'Danger - do not enter!',
    es: '¡Peligro - no entrar!',
    zh: '危险 - 禁止入内！',
    pt: 'Perigo - não entre!',
    fr: 'Danger - ne pas entrer!',
    ar: 'خطر - لا تدخل!',
    hi: 'खतरा - प्रवेश न करें!',
    vi: 'Nguy hiểm - không được vào!',
    ko: '위험 - 출입 금지!',
    pl: 'Niebezpieczeństwo - nie wchodzić!'
  },
  stop_work: {
    en: 'Stop work immediately',
    es: 'Detener el trabajo inmediatamente',
    zh: '立即停止工作',
    pt: 'Pare o trabalho imediatamente',
    fr: 'Arrêtez le travail immédiatement',
    ar: 'أوقف العمل فورا',
    hi: 'तुरंत काम बंद करें',
    vi: 'Ngừng làm việc ngay lập tức',
    ko: '즉시 작업 중지',
    pl: 'Natychmiast zatrzymaj pracę'
  }
};

const TranslationHub = () => {
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('chat');
  
  // User preferences
  const [userLanguage, setUserLanguage] = useState('en');
  
  // Chat state
  const [channels, setChannels] = useState([]);
  const [activeChannel, setActiveChannel] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [isRecording, setIsRecording] = useState(false);
  const [audioBlob, setAudioBlob] = useState(null);
  const mediaRecorderRef = useRef(null);
  const messagesEndRef = useRef(null);
  
  // Translation state
  const [sourceLanguage, setSourceLanguage] = useState('en');
  const [targetLanguage, setTargetLanguage] = useState('es');
  const [textToTranslate, setTextToTranslate] = useState('');
  const [translatedText, setTranslatedText] = useState('');
  const [isPlayingAudio, setIsPlayingAudio] = useState(false);
  const [voiceTranscription, setVoiceTranscription] = useState('');
  const [voiceTranslation, setVoiceTranslation] = useState('');
  const [isTranscribing, setIsTranscribing] = useState(false);
  
  // Image translation
  const [selectedImage, setSelectedImage] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [extractedText, setExtractedText] = useState('');
  const [translatedImageText, setTranslatedImageText] = useState('');
  
  // Emergency broadcast
  const [emergencyMessage, setEmergencyMessage] = useState('');
  const [selectedPhrase, setSelectedPhrase] = useState(null);
  
  // Offline packs
  const [offlinePacksInstalled, setOfflinePacksInstalled] = useState(0);

  // Telegram Integration
  const [telegramBotInfo, setTelegramBotInfo] = useState(null);
  const [telegramChatId, setTelegramChatId] = useState('');
  const [showTelegramModal, setShowTelegramModal] = useState(false);
  const [telegramLinking, setTelegramLinking] = useState(false);

  // WhatsApp Integration
  const [whatsappStatus, setWhatsappStatus] = useState(null);
  const [whatsappQR, setWhatsappQR] = useState(null);
  const [showWhatsAppModal, setShowWhatsAppModal] = useState(false);
  const [whatsappChatId, setWhatsappChatId] = useState('');
  const [whatsappLinking, setWhatsappLinking] = useState(false);

  // Slack Integration
  const [slackStatus, setSlackStatus] = useState(null);
  const [slackChannels, setSlackChannels] = useState([]);
  const [showSlackModal, setShowSlackModal] = useState(false);
  const [selectedSlackChannel, setSelectedSlackChannel] = useState('');
  const [slackLinking, setSlackLinking] = useState(false);

  // Microsoft Teams state
  const [teamsStatus, setTeamsStatus] = useState(null);
  const [showTeamsModal, setShowTeamsModal] = useState(false);
  const [teamsTeamId, setTeamsTeamId] = useState('');
  const [teamsChannelId, setTeamsChannelId] = useState('');
  const [teamsChannelName, setTeamsChannelName] = useState('');
  const [teamsLinking, setTeamsLinking] = useState(false);

  // Channel Creation Modal state
  const [showCreateChannelModal, setShowCreateChannelModal] = useState(false);
  const [newChannelName, setNewChannelName] = useState('');
  const [newChannelPlatform, setNewChannelPlatform] = useState('internal');
  const [newChannelConfig, setNewChannelConfig] = useState({});
  const [creatingChannel, setCreatingChannel] = useState(false);

  // Fetch user language preference
  useEffect(() => {
    fetchUserLanguage();
    fetchChannels();
    checkOfflineStatus();
    fetchTelegramBotInfo();
    fetchWhatsAppStatus();
    fetchSlackStatus();
    fetchTeamsStatus();
  }, []);

  // Scroll to top on component mount - CRITICAL FIX
  useEffect(() => {
    // Immediate scroll with multiple methods
    const scrollToTop = () => {
      // Standard methods
      window.scrollTo(0, 0);
      document.documentElement.scrollTop = 0;
      document.body.scrollTop = 0;
      
      // Scroll the main content area
      const mainContent = document.querySelector('main');
      if (mainContent) {
        mainContent.scrollTo(0, 0);
        mainContent.scrollTop = 0;
      }
      
      // Scroll any overflow containers
      const containers = document.querySelectorAll('.overflow-y-auto');
      containers.forEach(container => {
        container.scrollTop = 0;
      });
    };
    
    // Execute immediately
    scrollToTop();
    
    // Execute again after a tiny delay to override any other scroll behavior
    setTimeout(scrollToTop, 0);
    setTimeout(scrollToTop, 10);
    setTimeout(scrollToTop, 50);
  }, []);

  // Auto-scroll to bottom of messages (only when actively chatting)
  useEffect(() => {
    // Only auto-scroll if we're in the Team Chat tab and have an active channel
    if (activeTab === 'chat' && activeChannel && messages.length > 0) {
      messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages, activeTab, activeChannel]);

  const fetchUserLanguage = async () => {
    try {
      const response = await axios.get(`${API}/translation/user-language`);
      if (response.data.language) {
        setUserLanguage(response.data.language);
      }
    } catch (error) {
      console.error('Error fetching user language');
    }
  };

  const saveUserLanguage = async (languageCode) => {
    try {
      await axios.post(`${API}/translation/user-language`, { language: languageCode });
      setUserLanguage(languageCode);
      toast({
        title: "Success",
        description: `Language preference saved: ${LANGUAGES.find(l => l.code === languageCode)?.name}`
      });
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to save language preference",
        variant: "destructive"
      });
    }
  };

  const fetchChannels = async () => {
    try {
      const response = await axios.get(`${API}/translation/channels`);
      setChannels(response.data);
      if (response.data.length > 0 && !activeChannel) {
        setActiveChannel(response.data[0]);
        fetchMessages(response.data[0].id);
      }
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to fetch channels",
        variant: "destructive"
      });
    }
  };

  const fetchMessages = async (channelId) => {
    try {
      const response = await axios.get(`${API}/translation/channels/${channelId}/messages`);
      setMessages(response.data);
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to fetch messages",
        variant: "destructive"
      });
    }
  };

  const translateText = async (text, from, to) => {
    setLoading(true);
    try {
      const response = await axios.post(`${API}/translation/translate`, {
        text,
        sourceLanguage: from,
        targetLanguage: to
      });
      return response.data.translatedText;
    } catch (error) {
      toast({
        title: "Error",
        description: "Translation failed",
        variant: "destructive"
      });
      return text;
    } finally {
      setLoading(false);
    }
  };

  const sendMessage = async () => {
    if (!newMessage.trim() || !activeChannel) return;

    setLoading(true);
    try {
      const response = await axios.post(`${API}/translation/channels/${activeChannel.id}/messages`, {
        text: newMessage,
        originalLanguage: userLanguage
      });

      setMessages([...messages, response.data]);
      setNewMessage('');
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to send message",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mediaRecorder = new MediaRecorder(stream);
      const audioChunks = [];

      mediaRecorder.ondataavailable = (event) => {
        audioChunks.push(event.data);
      };

      mediaRecorder.onstop = () => {
        const audioBlob = new Blob(audioChunks, { type: 'audio/webm' });
        setAudioBlob(audioBlob);
        stream.getTracks().forEach(track => track.stop());
      };

      mediaRecorder.start();
      mediaRecorderRef.current = mediaRecorder;
      setIsRecording(true);
    } catch (error) {
      toast({
        title: "Error",
        description: "Could not access microphone",
        variant: "destructive"
      });
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
    }
  };

  const transcribeAndTranslateVoice = async () => {
    if (!audioBlob) return;

    setIsTranscribing(true);
    setVoiceTranscription('');
    setVoiceTranslation('');

    try {
      // Convert audio blob to base64
      const reader = new FileReader();
      reader.onloadend = async () => {
        const base64Audio = reader.result.split(',')[1];
        
        try {
          // Step 1: Transcribe audio
          const transcribeResponse = await axios.post(`${API}/ai/transcribe-audio`, {
            audio_data: base64Audio,
            audio_format: 'webm'
          });

          const transcribedText = transcribeResponse.data.transcription;
          setVoiceTranscription(transcribedText);

          // Step 2: Translate the transcription
          if (transcribedText) {
            const translateResponse = await axios.post(`${API}/translation/translate`, {
              text: transcribedText,
              sourceLanguage: sourceLanguage,
              targetLanguage: targetLanguage
            });

            setVoiceTranslation(translateResponse.data.translatedText);
          }

          toast({
            title: "Success",
            description: "Voice transcribed and translated successfully"
          });
        } catch (error) {
          console.error('Transcription/translation error:', error);
          toast({
            title: "Error",
            description: error.response?.data?.detail || "Failed to transcribe or translate voice",
            variant: "destructive"
          });
        } finally {
          setIsTranscribing(false);
        }
      };
      reader.readAsDataURL(audioBlob);
    } catch (error) {
      console.error('Error processing audio:', error);
      toast({
        title: "Error",
        description: "Failed to process audio recording",
        variant: "destructive"
      });
      setIsTranscribing(false);
    }
  };

  const sendVoiceMessage = async () => {
    if (!audioBlob || !activeChannel) return;

    setLoading(true);
    try {
      const reader = new FileReader();
      reader.onloadend = async () => {
        const base64Audio = reader.result.split(',')[1];
        
        const response = await axios.post(`${API}/translation/voice-message`, {
          audioData: base64Audio,
          channelId: activeChannel.id,
          originalLanguage: userLanguage
        });

        setMessages([...messages, response.data]);
        setAudioBlob(null);
        
        toast({
          title: "Success",
          description: "Voice message sent and translated"
        });
      };
      reader.readAsDataURL(audioBlob);
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to send voice message",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const playTranslatedAudio = async (text, language) => {
    setLoading(true);
    setIsPlayingAudio(true);
    try {
      const response = await axios.post(`${API}/translation/text-to-speech`, {
        text,
        language
      }, { responseType: 'blob' });

      const audioUrl = URL.createObjectURL(response.data);
      const audio = new Audio(audioUrl);
      
      audio.onended = () => {
        setIsPlayingAudio(false);
        URL.revokeObjectURL(audioUrl);
      };
      
      audio.play();
    } catch (error) {
      setIsPlayingAudio(false);
      toast({
        title: "Error",
        description: "Failed to play audio",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const handleTextTranslation = async () => {
    if (!textToTranslate.trim()) return;
    
    const translated = await translateText(textToTranslate, sourceLanguage, targetLanguage);
    setTranslatedText(translated);
  };

  const handleImageUpload = (event) => {
    const file = event.target.files[0];
    if (file) {
      setSelectedImage(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const translateImage = async () => {
    if (!selectedImage) return;

    setLoading(true);
    try {
      const reader = new FileReader();
      reader.onloadend = async () => {
        const base64Image = reader.result.split(',')[1];
        
        const response = await axios.post(`${API}/translation/image-translate`, {
          imageData: base64Image,
          targetLanguage: targetLanguage
        });

        setExtractedText(response.data.extractedText);
        setTranslatedImageText(response.data.translatedText);
        
        toast({
          title: "Success",
          description: "Image translated successfully"
        });
      };
      reader.readAsDataURL(selectedImage);
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to translate image",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const sendTranslationToChat = async (translationText) => {
    if (!activeChannel) {
      toast({
        title: "No Channel Selected",
        description: "Please select or create a channel in Team Chat first",
        variant: "destructive"
      });
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(`${API}/translation/channels/${activeChannel.id}/messages`, {
        text: translationText,
        originalLanguage: targetLanguage,
        isEmergency: false
      });

      setMessages([...messages, response.data]);
      
      toast({
        title: "Translation Sent",
        description: `Message sent to ${activeChannel.name}`,
        variant: "default"
      });
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to send message to chat",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const sendSafetyPhrase = async (phraseKey) => {
    if (!activeChannel) return;

    const phraseText = SAFETY_PHRASES[phraseKey][userLanguage];
    
    setLoading(true);
    try {
      const response = await axios.post(`${API}/translation/channels/${activeChannel.id}/messages`, {
        text: phraseText,
        originalLanguage: userLanguage,
        isEmergency: true
      });

      setMessages([...messages, response.data]);
      
      toast({
        title: "Emergency message sent",
        description: "All team members notified",
        variant: "default"
      });
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to send emergency message",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const sendEmergencyBroadcast = async () => {
    if (!emergencyMessage.trim()) return;

    setLoading(true);
    try {
      await axios.post(`${API}/translation/emergency-broadcast`, {
        message: emergencyMessage,
        sourceLanguage: userLanguage
      });

      setEmergencyMessage('');
      
      toast({
        title: "Emergency broadcast sent",
        description: "All users have been notified in their language"
      });
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to send broadcast",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const createChannel = async (name, type, platform = 'internal', platformConfig = null) => {
    setCreatingChannel(true);
    try {
      const requestData = {
        name,
        type,
        platform
      };
      
      if (platformConfig && Object.keys(platformConfig).length > 0) {
        requestData.platform_config = platformConfig;
      }
      
      const response = await axios.post(`${API}/translation/channels`, requestData);

      setChannels([...channels, response.data]);
      setActiveChannel(response.data);
      
      // Reset modal state
      setShowCreateChannelModal(false);
      setNewChannelName('');
      setNewChannelPlatform('internal');
      setNewChannelConfig({});
      
      toast({
        title: "Success",
        description: `Channel "${name}" created${platform !== 'internal' ? ` with ${platform} integration` : ''}`
      });
    } catch (error) {
      toast({
        title: "Error",
        description: error.response?.data?.detail || "Failed to create channel",
        variant: "destructive"
      });
    } finally {
      setCreatingChannel(false);
    }
  };

  const handleCreateChannel = () => {
    if (!newChannelName.trim()) {
      toast({
        title: "Error",
        description: "Please enter a channel name",
        variant: "destructive"
      });
      return;
    }
    
    createChannel(newChannelName, 'group', newChannelPlatform, newChannelConfig);
  };

  // Offline Translation Functions
  const EXTENDED_SAFETY_TRANSLATIONS = {
    // Common Construction Terms
    "hard hat": {
      en: "hard hat", es: "casco", zh: "安全帽", pt: "capacete", fr: "casque",
      ar: "خوذة صلبة", hi: "सुरक्षा टोपी", vi: "mũ cứng", ko: "안전모", pl: "kask"
    },
    "safety vest": {
      en: "safety vest", es: "chaleco de seguridad", zh: "安全背心", pt: "colete de segurança",
      fr: "gilet de sécurité", ar: "سترة الأمان", hi: "सुरक्षा बनियान", vi: "áo phao an toàn",
      ko: "안전 조끼", pl: "kamizelka odblaskowa"
    },
    "gloves": {
      en: "gloves", es: "guantes", zh: "手套", pt: "luvas", fr: "gants",
      ar: "قفازات", hi: "दस्ताने", vi: "găng tay", ko: "장갑", pl: "rękawice"
    },
    "goggles": {
      en: "goggles", es: "gafas de seguridad", zh: "护目镜", pt: "óculos", fr: "lunettes",
      ar: "نظارات واقية", hi: "चश्मा", vi: "kính bảo hộ", ko: "보안경", pl: "okulary ochronne"
    },
    "boots": {
      en: "safety boots", es: "botas de seguridad", zh: "安全靴", pt: "botas de segurança",
      fr: "bottes de sécurité", ar: "أحذية السلامة", hi: "सुरक्षा जूते", vi: "ủng an toàn",
      ko: "안전화", pl: "buty robocze"
    },
    // Warning Signs
    "danger": {
      en: "Danger", es: "Peligro", zh: "危险", pt: "Perigo", fr: "Danger",
      ar: "خطر", hi: "खतरा", vi: "Nguy hiểm", ko: "위험", pl: "Niebezpieczeństwo"
    },
    "warning": {
      en: "Warning", es: "Advertencia", zh: "警告", pt: "Aviso", fr: "Avertissement",
      ar: "تحذير", hi: "चेतावनी", vi: "Cảnh báo", ko: "경고", pl: "Ostrzeżenie"
    },
    "caution": {
      en: "Caution", es: "Precaución", zh: "小心", pt: "Cuidado", fr: "Attention",
      ar: "احتراس", hi: "सावधान", vi: "Thận trọng", ko: "주의", pl: "Uwaga"
    },
    // Common Phrases
    "stop": {
      en: "Stop", es: "Deténgase", zh: "停止", pt: "Pare", fr: "Arrêtez",
      ar: "قف", hi: "रुको", vi: "Dừng lại", ko: "정지", pl: "Stop"
    },
    "enter": {
      en: "Enter", es: "Entrar", zh: "进入", pt: "Entrar", fr: "Entrer",
      ar: "ادخل", hi: "प्रवेश करें", vi: "Vào", ko: "입장", pl: "Wejście"
    },
    "exit": {
      en: "Exit", es: "Salida", zh: "出口", pt: "Saída", fr: "Sortie",
      ar: "خروج", hi: "निकास", vi: "Lối ra", ko: "출구", pl: "Wyjście"
    },
    "help": {
      en: "Help", es: "Ayuda", zh: "帮助", pt: "Ajuda", fr: "Aide",
      ar: "مساعدة", hi: "मदद", vi: "Giúp đỡ", ko: "도움", pl: "Pomoc"
    },
    "emergency": {
      en: "Emergency", es: "Emergencia", zh: "紧急情况", pt: "Emergência", fr: "Urgence",
      ar: "طوارئ", hi: "आपातकाल", vi: "Khẩn cấp", ko: "비상사태", pl: "Nagły wypadek"
    },
    // Complete Safety Phrases
    "wear_ppe": {
      en: "Wear personal protective equipment",
      es: "Use equipo de protección personal",
      zh: "佩戴个人防护装备",
      pt: "Use equipamento de proteção individual",
      fr: "Porter un équipement de protection individuelle",
      ar: "ارتداء معدات الحماية الشخصية",
      hi: "व्यक्तिगत सुरक्षा उपकरण पहनें",
      vi: "Đeo thiết bị bảo hộ cá nhân",
      ko: "개인 보호 장비를 착용하세요",
      pl: "Nosić środki ochrony osobistej"
    },
    "no_entry": {
      en: "No entry without authorization",
      es: "No entrar sin autorización",
      zh: "未经授权禁止入内",
      pt: "Entrada proibida sem autorização",
      fr: "Entrée interdite sans autorisation",
      ar: "لا دخول بدون إذن",
      hi: "बिना अनुमति के प्रवेश नहीं",
      vi: "Cấm vào không có phép",
      ko: "허가 없이 출입 금지",
      pl: "Wstęp bez upoważnienia wzbroniony"
    },
    "first_aid": {
      en: "First aid station",
      es: "Estación de primeros auxilios",
      zh: "急救站",
      pt: "Posto de primeiros socorros",
      fr: "Poste de premiers secours",
      ar: "محطة الإسعافات الأولية",
      hi: "प्राथमिक चिकित्सा केंद्र",
      vi: "Trạm sơ cứu",
      ko: "응급처치실",
      pl: "Punkt pierwszej pomocy"
    },
    "fire_extinguisher": {
      en: "Fire extinguisher",
      es: "Extintor de incendios",
      zh: "灭火器",
      pt: "Extintor de incêndio",
      fr: "Extincteur",
      ar: "طفاية حريق",
      hi: "अग्निशामक यंत्र",
      vi: "Bình chữa cháy",
      ko: "소화기",
      pl: "Gaśnica"
    },
    "wet_floor": {
      en: "Wet floor - Watch your step",
      es: "Piso mojado - Cuidado al caminar",
      zh: "地面湿滑 - 小心脚下",
      pt: "Piso molhado - Cuidado ao andar",
      fr: "Sol mouillé - Attention où vous marchez",
      ar: "أرضية مبللة - احذر من السقوط",
      hi: "गीला फर्श - ध्यान से चलें",
      vi: "Sàn ướt - Cẩn thận khi đi",
      ko: "바닥이 젖음 - 발을 조심하세요",
      pl: "Mokra podłoga - Uważaj na krok"
    }
  };

  const downloadAllLanguagePacks = () => {
    try {
      // Combine safety phrases with extended translations
      const allTranslations = {
        ...SAFETY_PHRASES,
        ...EXTENDED_SAFETY_TRANSLATIONS
      };

      // Store in localStorage
      localStorage.setItem('offline_safety_translations', JSON.stringify(allTranslations));
      localStorage.setItem('offline_languages', JSON.stringify(LANGUAGES));
      localStorage.setItem('offline_pack_version', '1.0');
      localStorage.setItem('offline_pack_date', new Date().toISOString());

      // Create downloadable file
      const dataStr = JSON.stringify({
        version: '1.0',
        date: new Date().toISOString(),
        languages: LANGUAGES,
        translations: allTranslations
      }, null, 2);

      const dataBlob = new Blob([dataStr], { type: 'application/json' });
      const url = URL.createObjectURL(dataBlob);
      const link = document.createElement('a');
      link.href = url;
      link.download = 'safety-translation-pack.json';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      setOfflinePacksInstalled(LANGUAGES.length);

      toast({
        title: "Success!",
        description: `${LANGUAGES.length} language packs downloaded and installed for offline use`,
        duration: 5000
      });
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to download language packs",
        variant: "destructive"
      });
    }
  };

  const checkOfflineStatus = () => {
    try {
      const stored = localStorage.getItem('offline_safety_translations');
      if (stored) {
        const langs = JSON.parse(localStorage.getItem('offline_languages') || '[]');
        setOfflinePacksInstalled(langs.length);
        
        const packDate = localStorage.getItem('offline_pack_date');
        if (packDate) {
          const date = new Date(packDate);
          toast({
            title: "Offline Packs Available",
            description: `${langs.length} language packs installed on ${date.toLocaleDateString()}`,
            duration: 3000
          });
        }
      } else {
        setOfflinePacksInstalled(0);
        toast({
          title: "No Offline Packs",
          description: "Download language packs to use translations offline",
          duration: 3000
        });
      }
    } catch (error) {
      console.error('Error checking offline status:', error);
    }
  };

  // Helper function to get offline translation
  const getOfflineTranslation = (phraseKey, language) => {
    try {
      const stored = localStorage.getItem('offline_safety_translations');
      if (stored) {
        const translations = JSON.parse(stored);
        return translations[phraseKey]?.[language] || null;
      }
    } catch (error) {
      console.error('Error getting offline translation:', error);
    }
    return null;
  };

  // Telegram Integration Functions
  const fetchTelegramBotInfo = async () => {
    try {
      const response = await axios.get(`${API}/telegram/bot-info`);
      setTelegramBotInfo(response.data);
    } catch (error) {
      console.error('Error fetching Telegram bot info:', error);
      setTelegramBotInfo({ connected: false });
    }
  };

  const linkTelegramChannel = async () => {
    if (!activeChannel || !telegramChatId.trim()) {
      toast({
        title: "Error",
        description: "Please select a channel and enter a Telegram Chat ID",
        variant: "destructive"
      });
      return;
    }

    setTelegramLinking(true);
    try {
      const response = await axios.post(`${API}/telegram/link-channel`, {
        channel_id: activeChannel.id,
        telegram_chat_id: telegramChatId.trim()
      });

      toast({
        title: "Success!",
        description: response.data.message || "Telegram chat linked successfully"
      });

      setShowTelegramModal(false);
      setTelegramChatId('');
      fetchChannels(); // Refresh channels to show Telegram link status
    } catch (error) {
      toast({
        title: "Error",
        description: error.response?.data?.detail || "Failed to link Telegram chat",
        variant: "destructive"
      });
    } finally {
      setTelegramLinking(false);
    }
  };

  const unlinkTelegramChannel = async (channelId) => {
    try {
      await axios.post(`${API}/telegram/unlink-channel`, {
        channel_id: channelId
      });

      toast({
        title: "Success",
        description: "Telegram chat unlinked"
      });

      fetchChannels();
    } catch (error) {
      toast({
        title: "Error",
        description: error.response?.data?.detail || "Failed to unlink Telegram chat",
        variant: "destructive"
      });
    }
  };

  const testTelegramConnection = async () => {
    try {
      const response = await axios.get(`${API}/telegram/bot-info`);
      if (response.data?.connected) {
        toast({
          title: "✅ Telegram Connected",
          description: `Bot @${response.data.bot_username} is ready. Webhook is active.`
        });
      } else {
        toast({
          title: "❌ Connection Failed",
          description: response.data?.error || "Could not connect to Telegram",
          variant: "destructive"
        });
      }
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to check Telegram connection",
        variant: "destructive"
      });
    }
  };

  // WhatsApp Integration Functions
  const fetchWhatsAppStatus = async () => {
    try {
      const response = await axios.get(`${API}/whatsapp/status`);
      setWhatsappStatus(response.data);
    } catch (error) {
      console.error('Error fetching WhatsApp status:', error);
      setWhatsappStatus({ connected: false, status: 'service_unavailable' });
    }
  };

  const fetchWhatsAppQR = async () => {
    try {
      const response = await axios.get(`${API}/whatsapp/qr`);
      if (response.data?.qr) {
        setWhatsappQR(response.data.qr);
      }
    } catch (error) {
      console.error('Error fetching WhatsApp QR:', error);
    }
  };

  const linkWhatsAppChannel = async () => {
    if (!activeChannel || !whatsappChatId.trim()) {
      toast({
        title: "Error",
        description: "Please select a channel and enter a WhatsApp Chat ID",
        variant: "destructive"
      });
      return;
    }

    setWhatsappLinking(true);
    try {
      const response = await axios.post(`${API}/whatsapp/link-channel`, {
        channel_id: activeChannel.id,
        whatsapp_chat_id: whatsappChatId.trim()
      });

      toast({
        title: "Success!",
        description: response.data.message || "WhatsApp chat linked successfully"
      });

      setShowWhatsAppModal(false);
      setWhatsappChatId('');
      fetchChannels();
    } catch (error) {
      toast({
        title: "Error",
        description: error.response?.data?.detail || "Failed to link WhatsApp chat",
        variant: "destructive"
      });
    } finally {
      setWhatsappLinking(false);
    }
  };

  const unlinkWhatsAppChannel = async (channelId) => {
    try {
      await axios.post(`${API}/whatsapp/unlink-channel`, {
        channel_id: channelId
      });

      toast({
        title: "Success",
        description: "WhatsApp chat unlinked"
      });

      fetchChannels();
    } catch (error) {
      toast({
        title: "Error",
        description: error.response?.data?.detail || "Failed to unlink WhatsApp chat",
        variant: "destructive"
      });
    }
  };

  // Poll for WhatsApp QR code when modal is open
  useEffect(() => {
    let interval;
    if (showWhatsAppModal && !whatsappStatus?.connected) {
      fetchWhatsAppQR();
      interval = setInterval(() => {
        fetchWhatsAppQR();
        fetchWhatsAppStatus();
      }, 3000);
    }
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [showWhatsAppModal, whatsappStatus?.connected]);

  // Slack Integration Functions
  const fetchSlackStatus = async () => {
    try {
      const response = await axios.get(`${API}/slack/status`);
      setSlackStatus(response.data);
      if (response.data?.connected) {
        fetchSlackChannels();
      }
    } catch (error) {
      console.error('Error fetching Slack status:', error);
      setSlackStatus({ connected: false });
    }
  };

  const fetchSlackChannels = async () => {
    try {
      const response = await axios.get(`${API}/slack/channels`);
      setSlackChannels(response.data.channels || []);
    } catch (error) {
      console.error('Error fetching Slack channels:', error);
    }
  };

  const linkSlackChannel = async () => {
    if (!activeChannel || !selectedSlackChannel) {
      toast({
        title: "Error",
        description: "Please select a channel",
        variant: "destructive"
      });
      return;
    }

    setSlackLinking(true);
    try {
      const slackChannel = slackChannels.find(c => c.id === selectedSlackChannel);
      const response = await axios.post(`${API}/slack/link-channel`, {
        channel_id: activeChannel.id,
        slack_channel_id: selectedSlackChannel,
        slack_channel_name: slackChannel?.name
      });

      toast({
        title: "Success!",
        description: response.data.message || "Slack channel linked successfully"
      });

      setShowSlackModal(false);
      setSelectedSlackChannel('');
      fetchChannels();
    } catch (error) {
      toast({
        title: "Error",
        description: error.response?.data?.detail || "Failed to link Slack channel",
        variant: "destructive"
      });
    } finally {
      setSlackLinking(false);
    }
  };

  const unlinkSlackChannel = async (channelId) => {
    try {
      await axios.post(`${API}/slack/unlink-channel`, {
        channel_id: channelId
      });

      toast({
        title: "Success",
        description: "Slack channel unlinked"
      });

      fetchChannels();
    } catch (error) {
      toast({
        title: "Error",
        description: error.response?.data?.detail || "Failed to unlink Slack channel",
        variant: "destructive"
      });
    }
  };

  // Microsoft Teams Integration Functions
  const fetchTeamsStatus = async () => {
    try {
      const response = await axios.get(`${API}/teams/status`);
      setTeamsStatus(response.data);
    } catch (error) {
      console.error('Error fetching Teams status:', error);
      setTeamsStatus({ connected: false });
    }
  };

  const linkTeamsChannel = async () => {
    if (!activeChannel || !teamsTeamId || !teamsChannelId) {
      toast({
        title: "Error",
        description: "Please enter Team ID and Channel ID",
        variant: "destructive"
      });
      return;
    }

    setTeamsLinking(true);
    try {
      const response = await axios.post(`${API}/teams/link-channel`, {
        channel_id: activeChannel.id,
        teams_team_id: teamsTeamId,
        teams_channel_id: teamsChannelId,
        channel_name: teamsChannelName || 'Teams Channel'
      });

      toast({
        title: "Success!",
        description: response.data.message || "Microsoft Teams channel linked successfully"
      });

      setShowTeamsModal(false);
      setTeamsTeamId('');
      setTeamsChannelId('');
      setTeamsChannelName('');
      fetchChannels();
    } catch (error) {
      toast({
        title: "Error",
        description: error.response?.data?.detail || "Failed to link Teams channel",
        variant: "destructive"
      });
    } finally {
      setTeamsLinking(false);
    }
  };

  const unlinkTeamsChannel = async (channelId) => {
    try {
      await axios.post(`${API}/teams/unlink-channel`, {
        channel_id: channelId
      });

      toast({
        title: "Success",
        description: "Microsoft Teams channel unlinked"
      });

      fetchChannels();
    } catch (error) {
      toast({
        title: "Error",
        description: error.response?.data?.detail || "Failed to unlink Teams channel",
        variant: "destructive"
      });
    }
  };

  return (
    <div className="max-w-7xl mx-auto p-6 space-y-6" id="translation-hub-container">
      {/* Universal Page Header */}
      <PageHeader
        title="Translation Hub"
        subtitle="Breaking language barriers for safer workplaces"
        icon={Languages}
        variant="purple"
        actions={
          <div className="flex gap-2 items-center bg-white/10 backdrop-blur rounded-lg px-3 py-2">
            <Label className="text-sm text-white">My Language:</Label>
            <select
              value={userLanguage}
              onChange={(e) => saveUserLanguage(e.target.value)}
              className="border-0 rounded-md px-2 py-1 bg-white text-gray-900 text-sm"
            >
              {LANGUAGES.map(lang => (
                <option key={lang.code} value={lang.code}>
                  {lang.flag} {lang.name}
                </option>
              ))}
            </select>
          </div>
        }
      />

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <Card className="bg-gradient-to-br from-blue-500 to-blue-600 text-white">
          <CardContent className="p-6">
            <div className="flex items-center justify-between mb-2">
              <p className="text-blue-100 text-sm font-medium">Active Channels</p>
              <MessageSquare className="w-5 h-5 text-blue-100" />
            </div>
            <p className="text-3xl font-bold">{channels.length}</p>
            <p className="text-blue-100 text-sm mt-2">Communication channels</p>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-green-500 to-green-600 text-white">
          <CardContent className="p-6">
            <div className="flex items-center justify-between mb-2">
              <p className="text-green-100 text-sm font-medium">Languages Supported</p>
              <Globe className="w-5 h-5 text-green-100" />
            </div>
            <p className="text-3xl font-bold">{LANGUAGES.length}</p>
            <p className="text-green-100 text-sm mt-2">Real-time translation</p>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-purple-500 to-purple-600 text-white">
          <CardContent className="p-6">
            <div className="flex items-center justify-between mb-2">
              <p className="text-purple-100 text-sm font-medium">Messages Today</p>
              <Zap className="w-5 h-5 text-purple-100" />
            </div>
            <p className="text-3xl font-bold">{messages.length}</p>
            <p className="text-purple-100 text-sm mt-2">Translated messages</p>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-orange-500 to-orange-600 text-white">
          <CardContent className="p-6">
            <div className="flex items-center justify-between mb-2">
              <p className="text-orange-100 text-sm font-medium">Safety Phrases</p>
              <Shield className="w-5 h-5 text-orange-100" />
            </div>
            <p className="text-3xl font-bold">{Object.keys(SAFETY_PHRASES).length}</p>
            <p className="text-orange-100 text-sm mt-2">Quick access</p>
          </CardContent>
        </Card>
      </div>

      {/* Main Content Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
        <TabsList className="grid w-full grid-cols-5 h-auto p-1.5 gap-1 bg-gray-100 rounded-xl">
          <TabsTrigger 
            value="chat" 
            title="Team Chat"
            className="flex flex-col items-center gap-1 py-2.5 sm:py-3 px-1 sm:px-2 rounded-lg transition-all data-[state=active]:bg-blue-600 data-[state=active]:text-white data-[state=active]:shadow-md"
          >
            <Users className="w-5 h-5 sm:w-6 sm:h-6" />
            <span className="text-[10px] sm:text-xs font-medium">Chat</span>
          </TabsTrigger>
          <TabsTrigger 
            value="translate" 
            title="Text Translation"
            className="flex flex-col items-center gap-1 py-2.5 sm:py-3 px-1 sm:px-2 rounded-lg transition-all data-[state=active]:bg-blue-600 data-[state=active]:text-white data-[state=active]:shadow-md"
          >
            <FileText className="w-5 h-5 sm:w-6 sm:h-6" />
            <span className="text-[10px] sm:text-xs font-medium">Text</span>
          </TabsTrigger>
          <TabsTrigger 
            value="voice" 
            title="Voice Translation"
            className="flex flex-col items-center gap-1 py-2.5 sm:py-3 px-1 sm:px-2 rounded-lg transition-all data-[state=active]:bg-blue-600 data-[state=active]:text-white data-[state=active]:shadow-md"
          >
            <Mic className="w-5 h-5 sm:w-6 sm:h-6" />
            <span className="text-[10px] sm:text-xs font-medium">Voice</span>
          </TabsTrigger>
          <TabsTrigger 
            value="image" 
            title="Image Translation"
            className="flex flex-col items-center gap-1 py-2.5 sm:py-3 px-1 sm:px-2 rounded-lg transition-all data-[state=active]:bg-blue-600 data-[state=active]:text-white data-[state=active]:shadow-md"
          >
            <Camera className="w-5 h-5 sm:w-6 sm:h-6" />
            <span className="text-[10px] sm:text-xs font-medium">Image</span>
          </TabsTrigger>
          <TabsTrigger 
            value="safety" 
            title="Safety Phrases"
            className="flex flex-col items-center gap-1 py-2.5 sm:py-3 px-1 sm:px-2 rounded-lg transition-all data-[state=active]:bg-red-600 data-[state=active]:text-white data-[state=active]:shadow-md"
          >
            <Shield className="w-5 h-5 sm:w-6 sm:h-6" />
            <span className="text-[10px] sm:text-xs font-medium">Safety</span>
          </TabsTrigger>
        </TabsList>

        {/* Team Chat Tab */}
        <TabsContent value="chat">
          <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
            {/* Channel List */}
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle className="text-lg">Channels</CardTitle>
                  <Button size="sm" onClick={() => setShowCreateChannelModal(true)}>
                    <Plus className="w-4 h-4" />
                  </Button>
                </div>
                {/* Integration Status */}
                <div className="space-y-1 mt-2">
                  {telegramBotInfo?.connected && (
                    <div className="flex items-center gap-2 text-xs">
                      <div className="w-2 h-2 bg-blue-500 rounded-full animate-pulse" />
                      <span className="text-blue-700">📱 Telegram connected</span>
                    </div>
                  )}
                  {whatsappStatus?.connected && (
                    <div className="flex items-center gap-2 text-xs">
                      <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse" />
                      <span className="text-green-700">💬 WhatsApp connected</span>
                    </div>
                  )}
                  {slackStatus?.connected && (
                    <div className="flex items-center gap-2 text-xs">
                      <div className="w-2 h-2 bg-purple-500 rounded-full animate-pulse" />
                      <span className="text-purple-700">💼 Slack connected</span>
                    </div>
                  )}
                  {teamsStatus?.connected && (
                    <div className="flex items-center gap-2 text-xs">
                      <div className="w-2 h-2 bg-indigo-500 rounded-full animate-pulse" />
                      <span className="text-indigo-700">🟦 Teams connected</span>
                    </div>
                  )}
                </div>

                {/* Create Channel Modal */}
                {showCreateChannelModal && (
                  <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={() => setShowCreateChannelModal(false)}>
                    <div className="bg-white rounded-xl p-6 w-full max-w-md shadow-2xl" onClick={e => e.stopPropagation()}>
                      <h3 className="text-xl font-bold mb-4 flex items-center gap-2">
                        <Plus className="w-5 h-5" />
                        Create New Channel
                      </h3>
                      
                      <div className="space-y-4">
                        {/* Channel Name */}
                        <div>
                          <Label htmlFor="channelName">Channel Name</Label>
                          <Input
                            id="channelName"
                            placeholder="e.g., Safety Team, Site A Communication"
                            value={newChannelName}
                            onChange={(e) => setNewChannelName(e.target.value)}
                            className="mt-1"
                          />
                        </div>
                        
                        {/* Platform Selection */}
                        <div>
                          <Label>Platform Integration</Label>
                          <div className="grid grid-cols-2 gap-2 mt-2">
                            <button
                              type="button"
                              onClick={() => { setNewChannelPlatform('internal'); setNewChannelConfig({}); }}
                              className={`p-3 border rounded-lg text-center transition-all ${
                                newChannelPlatform === 'internal' 
                                  ? 'bg-gray-100 border-gray-400 ring-2 ring-gray-400' 
                                  : 'hover:bg-gray-50'
                              }`}
                            >
                              <Users className="w-6 h-6 mx-auto mb-1 text-gray-600" />
                              <span className="text-sm font-medium">Internal</span>
                            </button>
                            <button
                              type="button"
                              onClick={() => { setNewChannelPlatform('telegram'); setNewChannelConfig({}); }}
                              className={`p-3 border rounded-lg text-center transition-all ${
                                newChannelPlatform === 'telegram' 
                                  ? 'bg-blue-50 border-blue-400 ring-2 ring-blue-400' 
                                  : 'hover:bg-blue-50'
                              }`}
                            >
                              <span className="text-2xl">📱</span>
                              <span className="text-sm font-medium block">Telegram</span>
                            </button>
                            <button
                              type="button"
                              onClick={() => { setNewChannelPlatform('whatsapp'); setNewChannelConfig({}); }}
                              className={`p-3 border rounded-lg text-center transition-all ${
                                newChannelPlatform === 'whatsapp' 
                                  ? 'bg-green-50 border-green-400 ring-2 ring-green-400' 
                                  : 'hover:bg-green-50'
                              }`}
                            >
                              <span className="text-2xl">💬</span>
                              <span className="text-sm font-medium block">WhatsApp</span>
                            </button>
                            <button
                              type="button"
                              onClick={() => { setNewChannelPlatform('slack'); setNewChannelConfig({}); }}
                              className={`p-3 border rounded-lg text-center transition-all ${
                                newChannelPlatform === 'slack' 
                                  ? 'bg-purple-50 border-purple-400 ring-2 ring-purple-400' 
                                  : 'hover:bg-purple-50'
                              }`}
                            >
                              <span className="text-2xl">💼</span>
                              <span className="text-sm font-medium block">Slack</span>
                            </button>
                            <button
                              type="button"
                              onClick={() => { setNewChannelPlatform('teams'); setNewChannelConfig({}); }}
                              className={`p-3 border rounded-lg text-center transition-all col-span-2 ${
                                newChannelPlatform === 'teams' 
                                  ? 'bg-indigo-50 border-indigo-400 ring-2 ring-indigo-400' 
                                  : 'hover:bg-indigo-50'
                              }`}
                            >
                              <span className="text-2xl">🟦</span>
                              <span className="text-sm font-medium block">Microsoft Teams</span>
                            </button>
                          </div>
                        </div>
                        
                        {/* Platform-specific configuration */}
                        {newChannelPlatform === 'telegram' && (
                          <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg space-y-3">
                            <p className="text-sm text-blue-800">
                              📱 <strong>Telegram Setup:</strong> Add @{telegramBotInfo?.bot_username || 'YourBot'} to your group, then send /chatid to get the ID.
                            </p>
                            <Input
                              placeholder="Telegram Chat ID (e.g., -1001234567890)"
                              value={newChannelConfig.chat_id || ''}
                              onChange={(e) => setNewChannelConfig({...newChannelConfig, chat_id: e.target.value})}
                            />
                            <Input
                              placeholder="Chat Name (optional)"
                              value={newChannelConfig.chat_title || ''}
                              onChange={(e) => setNewChannelConfig({...newChannelConfig, chat_title: e.target.value})}
                            />
                          </div>
                        )}
                        
                        {newChannelPlatform === 'whatsapp' && (
                          <div className="p-4 bg-green-50 border border-green-200 rounded-lg space-y-3">
                            <p className="text-sm text-green-800">
                              💬 <strong>WhatsApp Setup:</strong> Enter the WhatsApp group/chat ID.
                            </p>
                            <Input
                              placeholder="WhatsApp Chat ID"
                              value={newChannelConfig.chat_id || ''}
                              onChange={(e) => setNewChannelConfig({...newChannelConfig, chat_id: e.target.value})}
                            />
                            <Input
                              placeholder="Chat Name (optional)"
                              value={newChannelConfig.chat_name || ''}
                              onChange={(e) => setNewChannelConfig({...newChannelConfig, chat_name: e.target.value})}
                            />
                          </div>
                        )}
                        
                        {newChannelPlatform === 'slack' && (
                          <div className="p-4 bg-purple-50 border border-purple-200 rounded-lg space-y-3">
                            <p className="text-sm text-purple-800">
                              💼 <strong>Slack Setup:</strong> Enter your Slack channel ID (starts with C).
                            </p>
                            <Input
                              placeholder="Slack Channel ID (e.g., C0123456789)"
                              value={newChannelConfig.channel_id || ''}
                              onChange={(e) => setNewChannelConfig({...newChannelConfig, channel_id: e.target.value})}
                            />
                            <Input
                              placeholder="Channel Name (optional)"
                              value={newChannelConfig.channel_name || ''}
                              onChange={(e) => setNewChannelConfig({...newChannelConfig, channel_name: e.target.value})}
                            />
                          </div>
                        )}
                        
                        {newChannelPlatform === 'teams' && (
                          <div className="p-4 bg-indigo-50 border border-indigo-200 rounded-lg space-y-3">
                            <p className="text-sm text-indigo-800">
                              🟦 <strong>Teams Setup:</strong> Get Team ID and Channel ID from Teams admin.
                            </p>
                            <Input
                              placeholder="Team ID"
                              value={newChannelConfig.team_id || ''}
                              onChange={(e) => setNewChannelConfig({...newChannelConfig, team_id: e.target.value})}
                            />
                            <Input
                              placeholder="Channel ID"
                              value={newChannelConfig.channel_id || ''}
                              onChange={(e) => setNewChannelConfig({...newChannelConfig, channel_id: e.target.value})}
                            />
                            <Input
                              placeholder="Channel Name (optional)"
                              value={newChannelConfig.channel_name || ''}
                              onChange={(e) => setNewChannelConfig({...newChannelConfig, channel_name: e.target.value})}
                            />
                          </div>
                        )}
                        
                        {newChannelPlatform === 'internal' && (
                          <div className="p-4 bg-gray-50 border border-gray-200 rounded-lg">
                            <p className="text-sm text-gray-600">
                              📝 Internal channels work within the Translation Hub only. You can link to external platforms later.
                            </p>
                          </div>
                        )}
                      </div>
                      
                      <div className="flex justify-end gap-3 mt-6">
                        <Button 
                          variant="outline" 
                          onClick={() => {
                            setShowCreateChannelModal(false);
                            setNewChannelName('');
                            setNewChannelPlatform('internal');
                            setNewChannelConfig({});
                          }}
                        >
                          Cancel
                        </Button>
                        <Button 
                          onClick={handleCreateChannel}
                          disabled={creatingChannel || !newChannelName.trim()}
                          className="bg-gradient-to-r from-purple-600 to-indigo-600"
                        >
                          {creatingChannel ? (
                            <>
                              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                              Creating...
                            </>
                          ) : (
                            <>
                              <Plus className="w-4 h-4 mr-2" />
                              Create Channel
                            </>
                          )}
                        </Button>
                      </div>
                    </div>
                  </div>
                )}
              </CardHeader>
              <CardContent className="p-0">
                <div className="space-y-1">
                  {channels.map((channel) => (
                    <button
                      key={channel.id}
                      onClick={() => {
                        setActiveChannel(channel);
                        fetchMessages(channel.id);
                      }}
                      className={`w-full text-left px-4 py-3 hover:bg-gray-50 ${
                        activeChannel?.id === channel.id ? 'bg-blue-50 border-r-2 border-blue-600' : ''
                      }`}
                    >
                      <div className="flex items-center gap-2">
                        <Users className="w-4 h-4" />
                        <span className="font-medium">{channel.name}</span>
                        {channel.telegram_linked && (
                          <span className="text-blue-500" title="Linked to Telegram">📱</span>
                        )}
                        {channel.whatsapp_linked && (
                          <span className="text-green-500" title="Linked to WhatsApp">💬</span>
                        )}
                        {channel.slack_linked && (
                          <span className="text-purple-500" title="Linked to Slack">💼</span>
                        )}
                        {channel.teams_linked && (
                          <span className="text-indigo-500" title="Linked to Teams">🟦</span>
                        )}
                      </div>
                      <p className="text-xs text-gray-500 mt-1">
                        {channel.memberCount} members
                        {channel.telegram_chat_title && (
                          <span className="ml-2 text-blue-600">• {channel.telegram_chat_title}</span>
                        )}
                        {channel.whatsapp_chat_name && (
                          <span className="ml-2 text-green-600">• {channel.whatsapp_chat_name}</span>
                        )}
                        {channel.slack_channel_name && (
                          <span className="ml-2 text-purple-600">• #{channel.slack_channel_name}</span>
                        )}
                        {channel.teams_channel_name && (
                          <span className="ml-2 text-indigo-600">• 🟦 {channel.teams_channel_name}</span>
                        )}
                      </p>
                    </button>
                  ))}
                  {channels.length === 0 && (
                    <div className="text-center py-8 text-gray-500 text-sm">
                      No channels yet. Create one to start!
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Chat Area */}
            <Card className="lg:col-span-3">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle>
                      {activeChannel ? activeChannel.name : 'Select a channel'}
                    </CardTitle>
                    <CardDescription>
                      All messages are automatically translated to your language ({LANGUAGES.find(l => l.code === userLanguage)?.name})
                    </CardDescription>
                  </div>
                  {/* Integration Link/Unlink Buttons */}
                  {activeChannel && (
                    <div className="flex gap-2">
                      {/* Telegram Button */}
                      {telegramBotInfo?.connected && (
                        activeChannel.telegram_linked ? (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => unlinkTelegramChannel(activeChannel.id)}
                            className="text-red-600 border-red-200 hover:bg-red-50"
                          >
                            <Unlink className="w-4 h-4 mr-1" />
                            <span className="hidden sm:inline">Unlink</span> 📱
                          </Button>
                        ) : (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => setShowTelegramModal(true)}
                            className="text-blue-600 border-blue-200 hover:bg-blue-50"
                          >
                            <Link className="w-4 h-4 mr-1" />
                            📱 <span className="hidden sm:inline">Telegram</span>
                          </Button>
                        )
                      )}
                      {/* WhatsApp Button */}
                      {whatsappStatus?.connected ? (
                        activeChannel.whatsapp_linked ? (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => unlinkWhatsAppChannel(activeChannel.id)}
                            className="text-red-600 border-red-200 hover:bg-red-50"
                          >
                            <Unlink className="w-4 h-4 mr-1" />
                            <span className="hidden sm:inline">Unlink</span> 💬
                          </Button>
                        ) : (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => setShowWhatsAppModal(true)}
                            className="text-green-600 border-green-200 hover:bg-green-50"
                          >
                            <Link className="w-4 h-4 mr-1" />
                            💬 <span className="hidden sm:inline">WhatsApp</span>
                          </Button>
                        )
                      ) : (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setShowWhatsAppModal(true)}
                          className="text-green-600 border-green-200 hover:bg-green-50"
                        >
                          💬 <span className="hidden sm:inline">Connect WhatsApp</span>
                        </Button>
                      )}
                      {/* Slack Button */}
                      {slackStatus?.connected ? (
                        activeChannel.slack_linked ? (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => unlinkSlackChannel(activeChannel.id)}
                            className="text-red-600 border-red-200 hover:bg-red-50"
                          >
                            <Unlink className="w-4 h-4 mr-1" />
                            <span className="hidden sm:inline">Unlink</span> 💼
                          </Button>
                        ) : (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => { setShowSlackModal(true); fetchSlackChannels(); }}
                            className="text-purple-600 border-purple-200 hover:bg-purple-50"
                          >
                            <Link className="w-4 h-4 mr-1" />
                            💼 <span className="hidden sm:inline">Slack</span>
                          </Button>
                        )
                      ) : (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setShowSlackModal(true)}
                          className="text-purple-600 border-purple-200 hover:bg-purple-50"
                          title="Configure Slack in settings"
                        >
                          💼 <span className="hidden sm:inline">Slack</span>
                        </Button>
                      )}
                      {/* Microsoft Teams Button */}
                      {teamsStatus?.connected ? (
                        activeChannel.teams_linked ? (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => unlinkTeamsChannel(activeChannel.id)}
                            className="text-red-600 border-red-200 hover:bg-red-50"
                          >
                            <Unlink className="w-4 h-4 mr-1" />
                            <span className="hidden sm:inline">Unlink</span> 🟦
                          </Button>
                        ) : (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => setShowTeamsModal(true)}
                            className="text-indigo-600 border-indigo-200 hover:bg-indigo-50"
                          >
                            <Link className="w-4 h-4 mr-1" />
                            🟦 <span className="hidden sm:inline">Teams</span>
                          </Button>
                        )
                      ) : (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setShowTeamsModal(true)}
                          className="text-indigo-600 border-indigo-200 hover:bg-indigo-50"
                          title="Configure Microsoft Teams"
                        >
                          🟦 <span className="hidden sm:inline">Teams</span>
                        </Button>
                      )}
                    </div>
                  )}
                </div>
              </CardHeader>
              <CardContent>
                {activeChannel ? (
                  <>
                    {/* Telegram Link Modal */}
                    {showTelegramModal && (
                      <div className="mb-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                        <h4 className="font-semibold text-blue-900 mb-2 flex items-center gap-2">
                          <span>📱</span> Link Telegram Chat
                        </h4>
                        <p className="text-sm text-blue-800 mb-3">
                          To link a Telegram group/chat:
                          <br />1. Add @{telegramBotInfo?.bot_username} to your Telegram group
                          <br />2. Send /chatid in the group to get the Chat ID
                          <br />3. Paste the Chat ID below
                        </p>
                        <div className="flex gap-2">
                          <Input
                            placeholder="Enter Telegram Chat ID (e.g., -1001234567890)"
                            value={telegramChatId}
                            onChange={(e) => setTelegramChatId(e.target.value)}
                            className="flex-1"
                          />
                          <Button onClick={linkTelegramChannel} disabled={telegramLinking}>
                            {telegramLinking ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Link'}
                          </Button>
                          <Button variant="outline" onClick={() => setShowTelegramModal(false)}>
                            Cancel
                          </Button>
                        </div>
                      </div>
                    )}

                    {/* Telegram Connected Banner */}
                    {activeChannel.telegram_linked && (
                      <div className="mb-4 p-2 bg-blue-50 border border-blue-200 rounded-lg flex items-center gap-2 text-sm text-blue-800">
                        <span>📱</span>
                        <span>Telegram: <strong>{activeChannel.telegram_chat_title}</strong></span>
                        <span className="ml-auto text-xs text-blue-600">Synced</span>
                      </div>
                    )}

                    {/* WhatsApp Link Modal */}
                    {showWhatsAppModal && (
                      <div className="mb-4 p-4 bg-green-50 border border-green-200 rounded-lg">
                        <h4 className="font-semibold text-green-900 mb-2 flex items-center gap-2">
                          <span>💬</span> Connect WhatsApp
                        </h4>
                        {!whatsappStatus?.connected ? (
                          <div className="text-center">
                            <p className="text-sm text-green-800 mb-3">
                              Scan the QR code with your WhatsApp to connect:
                            </p>
                            {whatsappQR ? (
                              <img src={whatsappQR} alt="WhatsApp QR Code" className="mx-auto w-48 h-48 border rounded-lg" />
                            ) : (
                              <div className="flex items-center justify-center w-48 h-48 mx-auto border rounded-lg bg-white">
                                <Loader2 className="w-8 h-8 animate-spin text-green-600" />
                              </div>
                            )}
                            <p className="text-xs text-green-600 mt-2">
                              Open WhatsApp → Settings → Linked Devices → Link a Device
                            </p>
                          </div>
                        ) : (
                          <div>
                            <p className="text-sm text-green-800 mb-3">
                              ✅ WhatsApp connected! Enter the phone number or group name to link:
                            </p>
                            <div className="flex gap-2">
                              <Input
                                placeholder="Phone number (e.g., 1234567890@s.whatsapp.net)"
                                value={whatsappChatId}
                                onChange={(e) => setWhatsappChatId(e.target.value)}
                                className="flex-1"
                              />
                              <Button onClick={linkWhatsAppChannel} disabled={whatsappLinking}>
                                {whatsappLinking ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Link'}
                              </Button>
                            </div>
                          </div>
                        )}
                        <Button variant="ghost" size="sm" onClick={() => setShowWhatsAppModal(false)} className="mt-2">
                          Cancel
                        </Button>
                      </div>
                    )}

                    {/* WhatsApp Connected Banner */}
                    {activeChannel.whatsapp_linked && (
                      <div className="mb-4 p-2 bg-green-50 border border-green-200 rounded-lg flex items-center gap-2 text-sm text-green-800">
                        <span>💬</span>
                        <span>WhatsApp: <strong>{activeChannel.whatsapp_chat_name}</strong></span>
                        <span className="ml-auto text-xs text-green-600">Synced</span>
                      </div>
                    )}

                    {/* Slack Link Modal */}
                    {showSlackModal && (
                      <div className="mb-4 p-4 bg-purple-50 border border-purple-200 rounded-lg">
                        <h4 className="font-semibold text-purple-900 mb-2 flex items-center gap-2">
                          <span>💼</span> Link Slack Channel
                        </h4>
                        {!slackStatus?.connected ? (
                          <div>
                            <p className="text-sm text-purple-800 mb-3">
                              Slack is not connected. Please configure Slack credentials:
                            </p>
                            <ol className="text-sm text-purple-700 list-decimal list-inside space-y-1">
                              <li>Go to <a href="https://api.slack.com/apps" target="_blank" rel="noopener noreferrer" className="underline">api.slack.com/apps</a></li>
                              <li>Create a new app or select existing</li>
                              <li>Get Bot Token (xoxb-...) and Signing Secret</li>
                              <li>Add to environment variables</li>
                            </ol>
                          </div>
                        ) : (
                          <div>
                            <p className="text-sm text-purple-800 mb-3">
                              Select a Slack channel to link:
                            </p>
                            <div className="flex gap-2">
                              <select
                                value={selectedSlackChannel}
                                onChange={(e) => setSelectedSlackChannel(e.target.value)}
                                className="flex-1 px-3 py-2 border rounded-md"
                              >
                                <option value="">Select a channel...</option>
                                {slackChannels.map(ch => (
                                  <option key={ch.id} value={ch.id}>
                                    #{ch.name} {ch.is_private ? '🔒' : ''}
                                  </option>
                                ))}
                              </select>
                              <Button onClick={linkSlackChannel} disabled={slackLinking || !selectedSlackChannel}>
                                {slackLinking ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Link'}
                              </Button>
                            </div>
                          </div>
                        )}
                        <Button variant="ghost" size="sm" onClick={() => setShowSlackModal(false)} className="mt-2">
                          Cancel
                        </Button>
                      </div>
                    )}

                    {/* Slack Connected Banner */}
                    {activeChannel.slack_linked && (
                      <div className="mb-4 p-2 bg-purple-50 border border-purple-200 rounded-lg flex items-center gap-2 text-sm text-purple-800">
                        <span>💼</span>
                        <span>Slack: <strong>#{activeChannel.slack_channel_name}</strong></span>
                        <span className="ml-auto text-xs text-purple-600">Synced</span>
                      </div>
                    )}

                    {/* Microsoft Teams Link Modal */}
                    {showTeamsModal && (
                      <div className="mb-4 p-4 bg-indigo-50 border border-indigo-200 rounded-lg">
                        <h4 className="font-semibold text-indigo-900 mb-2 flex items-center gap-2">
                          <span>🟦</span> Link Microsoft Teams Channel
                        </h4>
                        {!teamsStatus?.connected ? (
                          <div>
                            <p className="text-sm text-indigo-800 mb-3">
                              Microsoft Teams integration requires Azure Bot Framework setup:
                            </p>
                            <ol className="text-sm text-indigo-700 list-decimal list-inside space-y-1">
                              <li>Go to <a href="https://portal.azure.com" target="_blank" rel="noopener noreferrer" className="underline">Azure Portal</a></li>
                              <li>Create an Azure Bot resource</li>
                              <li>Get the Microsoft App ID and create a client secret</li>
                              <li>Set messaging endpoint to your webhook URL</li>
                              <li>Add credentials to environment variables</li>
                            </ol>
                          </div>
                        ) : (
                          <div className="space-y-3">
                            <p className="text-sm text-indigo-800">
                              Enter your Teams channel details:
                            </p>
                            <div className="grid grid-cols-1 gap-2">
                              <Input
                                placeholder="Team ID (e.g., 19:abc123@thread.tacv2)"
                                value={teamsTeamId}
                                onChange={(e) => setTeamsTeamId(e.target.value)}
                              />
                              <Input
                                placeholder="Channel ID (e.g., 19:xyz789@thread.tacv2)"
                                value={teamsChannelId}
                                onChange={(e) => setTeamsChannelId(e.target.value)}
                              />
                              <Input
                                placeholder="Channel Name (optional, e.g., General)"
                                value={teamsChannelName}
                                onChange={(e) => setTeamsChannelName(e.target.value)}
                              />
                            </div>
                            <div className="flex gap-2">
                              <Button onClick={linkTeamsChannel} disabled={teamsLinking || !teamsTeamId || !teamsChannelId}>
                                {teamsLinking ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Link Channel'}
                              </Button>
                              <Button variant="outline" onClick={() => setShowTeamsModal(false)}>
                                Cancel
                              </Button>
                            </div>
                          </div>
                        )}
                        {!teamsStatus?.connected && (
                          <Button variant="ghost" size="sm" onClick={() => setShowTeamsModal(false)} className="mt-2">
                            Cancel
                          </Button>
                        )}
                      </div>
                    )}

                    {/* Microsoft Teams Connected Banner */}
                    {activeChannel.teams_linked && (
                      <div className="mb-4 p-2 bg-indigo-50 border border-indigo-200 rounded-lg flex items-center gap-2 text-sm text-indigo-800">
                        <span>🟦</span>
                        <span>Teams: <strong>{activeChannel.teams_channel_name}</strong></span>
                        <span className="ml-auto text-xs text-indigo-600">Synced</span>
                      </div>
                    )}

                    {/* Messages */}
                    <div className="h-96 overflow-y-auto border rounded-lg p-4 mb-4 space-y-4 bg-gray-50">
                      {Array.isArray(messages) && messages.map((message, index) => (
                        <div
                          key={index}
                          className={`flex ${message.isCurrentUser ? 'justify-end' : 'justify-start'}`}
                        >
                          <div
                            className={`max-w-xs lg:max-w-md px-4 py-2 rounded-lg ${
                              message.isCurrentUser
                                ? 'bg-blue-600 text-white'
                                : message.source === 'telegram'
                                  ? 'bg-blue-100 border border-blue-200'
                                  : message.source === 'whatsapp'
                                    ? 'bg-green-100 border border-green-200'
                                    : message.source === 'slack'
                                      ? 'bg-purple-100 border border-purple-200'
                                      : message.source === 'teams'
                                        ? 'bg-indigo-100 border border-indigo-200'
                                        : 'bg-white border border-gray-200'
                            }`}
                          >
                            <p className="text-sm font-medium mb-1">
                              {message.senderName} 
                              <span className="ml-2 text-xs opacity-70">
                                {message.source === 'telegram' ? '📱' : message.source === 'whatsapp' ? '💬' : message.source === 'slack' ? '💼' : message.source === 'teams' ? '🟦' : LANGUAGES.find(l => l.code === message.originalLanguage)?.flag}
                              </span>
                            </p>
                            <p className="text-sm">{message.translatedText || message.originalText}</p>
                            {message.originalText !== message.translatedText && (
                              <p className="text-xs opacity-70 mt-1 italic">
                                Original: {message.originalText}
                              </p>
                            )}
                            <div className="flex items-center justify-between mt-2">
                              <span className="text-xs opacity-70">
                                {new Date(message.timestamp).toLocaleTimeString()}
                              </span>
                              {message.translatedText && (
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  className="h-6 w-6 p-0"
                                  onClick={() => playTranslatedAudio(message.translatedText, userLanguage)}
                                >
                                  <Volume2 className="w-3 h-3" />
                                </Button>
                              )}
                            </div>
                            {message.isEmergency && (
                              <Badge className="mt-2 bg-red-600">
                                <AlertTriangle className="w-3 h-3 mr-1" />
                                Emergency
                              </Badge>
                            )}
                          </div>
                        </div>
                      ))}
                      <div ref={messagesEndRef} />
                    </div>

                    {/* Voice Recording UI */}
                    {audioBlob && (
                      <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <Mic className="w-5 h-5 text-blue-600" />
                          <span className="text-sm text-blue-900">Voice message recorded</span>
                        </div>
                        <div className="flex gap-2">
                          <Button size="sm" onClick={sendVoiceMessage} disabled={loading}>
                            <Send className="w-4 h-4 mr-2" />
                            Send
                          </Button>
                          <Button size="sm" variant="outline" onClick={() => setAudioBlob(null)}>
                            <X className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    )}

                    {/* Message Input */}
                    <div className="flex gap-2">
                      <Button
                        variant={isRecording ? "destructive" : "outline"}
                        size="icon"
                        onClick={isRecording ? stopRecording : startRecording}
                      >
                        {isRecording ? <Pause className="w-4 h-4" /> : <Mic className="w-4 h-4" />}
                      </Button>
                      <Input
                        placeholder={`Type message in ${LANGUAGES.find(l => l.code === userLanguage)?.name}...`}
                        value={newMessage}
                        onChange={(e) => setNewMessage(e.target.value)}
                        onKeyPress={(e) => e.key === 'Enter' && sendMessage()}
                      />
                      <Button onClick={sendMessage} disabled={loading || !newMessage.trim()}>
                        <Send className="w-4 h-4" />
                      </Button>
                    </div>
                  </>
                ) : (
                  <div className="text-center py-16 text-gray-500">
                    <MessageSquare className="w-16 h-16 mx-auto mb-4 opacity-50" />
                    <p>Select a channel to start chatting</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Text Translation Tab */}
        <TabsContent value="translate">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center">
                <Languages className="w-5 h-5 mr-2 text-blue-600" />
                Text Translation
              </CardTitle>
              <CardDescription>Translate text between any two languages</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label>From Language</Label>
                  <select
                    value={sourceLanguage}
                    onChange={(e) => setSourceLanguage(e.target.value)}
                    className="w-full border rounded-md p-2 mt-1"
                  >
                    {LANGUAGES.map(lang => (
                      <option key={lang.code} value={lang.code}>
                        {lang.flag} {lang.name}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <Label>To Language</Label>
                  <select
                    value={targetLanguage}
                    onChange={(e) => setTargetLanguage(e.target.value)}
                    className="w-full border rounded-md p-2 mt-1"
                  >
                    {LANGUAGES.map(lang => (
                      <option key={lang.code} value={lang.code}>
                        {lang.flag} {lang.name}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label>Original Text</Label>
                  <Textarea
                    value={textToTranslate}
                    onChange={(e) => setTextToTranslate(e.target.value)}
                    placeholder="Enter text to translate..."
                    rows={8}
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label>Translated Text</Label>
                  <Textarea
                    value={translatedText}
                    readOnly
                    placeholder="Translation will appear here..."
                    rows={8}
                    className="mt-1 bg-blue-50 font-medium"
                  />
                </div>
              </div>

              <Button onClick={handleTextTranslation} disabled={loading || !textToTranslate.trim()}>
                <Languages className="w-4 h-4 mr-2" />
                {loading ? 'Translating...' : 'Translate'}
              </Button>

              {/* Action Buttons - Show after translation */}
              {translatedText && !loading && (
                <Card className="bg-gradient-to-r from-blue-50 to-indigo-50 border-blue-200 mt-4">
                  <CardContent className="pt-6">
                    <h4 className="font-semibold text-gray-900 mb-3">What would you like to do with this translation?</h4>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      <Button
                        variant="default"
                        className="w-full"
                        onClick={() => playTranslatedAudio(translatedText, targetLanguage)}
                        disabled={isPlayingAudio}
                      >
                        <Volume2 className="w-4 h-4 mr-2" />
                        {isPlayingAudio ? 'Playing...' : 'Play Audio (Read Aloud)'}
                      </Button>
                      <Button
                        variant="outline"
                        className="w-full"
                        onClick={() => {
                          if (activeChannel) {
                            sendTranslationToChat(translatedText);
                          } else {
                            toast({
                              title: "Select a Channel First",
                              description: "Go to Team Chat tab and select or create a channel to send this message",
                              variant: "destructive"
                            });
                          }
                        }}
                      >
                        <MessageSquare className="w-4 h-4 mr-2" />
                        Send to Team Chat
                      </Button>
                    </div>
                    <p className="text-xs text-gray-600 mt-3 text-center">
                      Translation is displayed above. Choose an action to continue.
                    </p>
                  </CardContent>
                </Card>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Voice Translation Tab */}
        <TabsContent value="voice">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center">
                <Mic className="w-5 h-5 mr-2 text-blue-600" />
                Voice Translation
              </CardTitle>
              <CardDescription>Record voice in any language and get instant translation</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Language Selection */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label>From Language (What you&apos;ll speak)</Label>
                  <select
                    value={sourceLanguage}
                    onChange={(e) => setSourceLanguage(e.target.value)}
                    className="w-full border rounded-md p-2 mt-1"
                  >
                    {LANGUAGES.map(lang => (
                      <option key={lang.code} value={lang.code}>
                        {lang.flag} {lang.name}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <Label>To Language (Translation target)</Label>
                  <select
                    value={targetLanguage}
                    onChange={(e) => setTargetLanguage(e.target.value)}
                    className="w-full border rounded-md p-2 mt-1"
                  >
                    {LANGUAGES.map(lang => (
                      <option key={lang.code} value={lang.code}>
                        {lang.flag} {lang.name}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Recording Interface */}
              <div className="text-center py-6 bg-gray-50 rounded-lg">
                <div className="inline-block p-6 bg-blue-50 rounded-full mb-4">
                  <Mic className={`w-16 h-16 ${isRecording ? 'text-red-600 animate-pulse' : 'text-blue-600'}`} />
                </div>
                <h3 className="text-lg font-semibold mb-2">
                  {isRecording ? 'Recording...' : 'Ready to Record'}
                </h3>
                <p className="text-gray-600 mb-4 text-sm">
                  {isRecording 
                    ? 'Speak clearly in your selected language. Click stop when finished.'
                    : 'Click the button below to start recording your voice'}
                </p>
                <div className="flex gap-2 justify-center">
                  <Button
                    size="lg"
                    variant={isRecording ? "destructive" : "default"}
                    onClick={isRecording ? stopRecording : startRecording}
                  >
                    {isRecording ? (
                      <>
                        <Pause className="w-5 h-5 mr-2" />
                        Stop Recording
                      </>
                    ) : (
                      <>
                        <Mic className="w-5 h-5 mr-2" />
                        Start Recording
                      </>
                    )}
                  </Button>
                  {audioBlob && !isRecording && (
                    <Button variant="outline" onClick={() => {
                      setAudioBlob(null);
                      setVoiceTranscription('');
                      setVoiceTranslation('');
                    }}>
                      <X className="w-4 h-4 mr-2" />
                      Clear
                    </Button>
                  )}
                </div>
              </div>

              {/* Process Recording Button */}
              {audioBlob && !voiceTranscription && (
                <div className="text-center">
                  <Button
                    size="lg"
                    onClick={transcribeAndTranslateVoice}
                    disabled={isTranscribing}
                    className="w-full md:w-auto"
                  >
                    {isTranscribing ? (
                      <>
                        <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                        Transcribing & Translating...
                      </>
                    ) : (
                      <>
                        <Languages className="w-5 h-5 mr-2" />
                        Transcribe & Translate
                      </>
                    )}
                  </Button>
                </div>
              )}

              {/* Transcription and Translation Results */}
              {voiceTranscription && (
                <div className="space-y-4 border-t pt-6">
                  <h4 className="font-semibold text-lg mb-4">Voice Translation Results</h4>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <Label>What You Said (Transcription)</Label>
                      <Textarea
                        value={voiceTranscription}
                        readOnly
                        rows={6}
                        className="mt-1 bg-gray-50 font-medium"
                      />
                    </div>
                    <div>
                      <Label>Translated Text</Label>
                      <Textarea
                        value={voiceTranslation}
                        readOnly
                        rows={6}
                        className="mt-1 bg-blue-50 font-medium"
                      />
                    </div>
                  </div>

                  {/* Action Buttons */}
                  {voiceTranslation && (
                    <Card className="bg-gradient-to-r from-blue-50 to-indigo-50 border-blue-200">
                      <CardContent className="pt-6">
                        <h4 className="font-semibold text-gray-900 mb-3">What would you like to do with this translation?</h4>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                          <Button
                            variant="default"
                            className="w-full"
                            onClick={() => playTranslatedAudio(voiceTranslation, targetLanguage)}
                            disabled={isPlayingAudio}
                          >
                            <Volume2 className="w-4 h-4 mr-2" />
                            {isPlayingAudio ? 'Playing...' : 'Play Audio (Read Aloud)'}
                          </Button>
                          <Button
                            variant="outline"
                            className="w-full"
                            onClick={() => {
                              if (activeChannel) {
                                sendTranslationToChat(voiceTranslation);
                              } else {
                                toast({
                                  title: "Select a Channel First",
                                  description: "Go to Team Chat tab and select or create a channel to send this message",
                                  variant: "destructive"
                                });
                              }
                            }}
                          >
                            <MessageSquare className="w-4 h-4 mr-2" />
                            Send to Team Chat
                          </Button>
                        </div>
                        <p className="text-xs text-gray-600 mt-3 text-center">
                          Translation is displayed above. Choose an action to continue.
                        </p>
                      </CardContent>
                    </Card>
                  )}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Image Translation Tab */}
        <TabsContent value="image">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center">
                <Camera className="w-5 h-5 mr-2 text-blue-600" />
                Image Translation
              </CardTitle>
              <CardDescription>
                Upload images of signs, documents, or equipment manuals for translation
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div>
                <Label>Target Language</Label>
                <select
                  value={targetLanguage}
                  onChange={(e) => setTargetLanguage(e.target.value)}
                  className="w-full md:w-64 border rounded-md p-2 mt-1"
                >
                  {LANGUAGES.map(lang => (
                    <option key={lang.code} value={lang.code}>
                      {lang.flag} {lang.name}
                    </option>
                  ))}
                </select>
              </div>

              <div className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center">
                <Input
                  type="file"
                  accept="image/*"
                  onChange={handleImageUpload}
                  className="hidden"
                  id="image-upload"
                />
                <label htmlFor="image-upload" className="cursor-pointer">
                  <ImageIcon className="w-16 h-16 mx-auto mb-4 text-gray-400" />
                  <p className="text-lg font-medium mb-2">Upload Image</p>
                  <p className="text-sm text-gray-600">
                    Click to upload or drag and drop
                  </p>
                  <p className="text-xs text-gray-500 mt-2">
                    Supports: JPG, PNG, HEIC (Max 10MB)
                  </p>
                </label>
              </div>

              {imagePreview && (
                <div className="space-y-4">
                  <div>
                    <Label>Uploaded Image</Label>
                    <img
                      src={imagePreview}
                      alt="Preview"
                      className="mt-2 max-w-md rounded-lg border"
                    />
                  </div>

                  <Button onClick={translateImage} disabled={loading}>
                    <Languages className="w-4 h-4 mr-2" />
                    {loading ? 'Translating...' : 'Translate Image'}
                  </Button>

                  {extractedText && (
                    <div className="space-y-4">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <Label>Extracted Text (Original)</Label>
                          <Textarea
                            value={extractedText}
                            readOnly
                            rows={6}
                            className="mt-1 bg-gray-50"
                          />
                        </div>
                        <div>
                          <Label>Translated Text</Label>
                          <Textarea
                            value={translatedImageText}
                            readOnly
                            rows={6}
                            className="mt-1 bg-blue-50 font-medium"
                          />
                        </div>
                      </div>
                      
                      {/* Action Buttons */}
                      {translatedImageText && (
                        <Card className="bg-gradient-to-r from-blue-50 to-indigo-50 border-blue-200">
                          <CardContent className="pt-6">
                            <h4 className="font-semibold text-gray-900 mb-3">What would you like to do with this translation?</h4>
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                              <Button
                                variant="default"
                                className="w-full"
                                onClick={() => playTranslatedAudio(translatedImageText, targetLanguage)}
                              >
                                <Volume2 className="w-4 h-4 mr-2" />
                                Play Audio (Read Aloud)
                              </Button>
                              <Button
                                variant="outline"
                                className="w-full"
                                onClick={() => {
                                  if (activeChannel) {
                                    sendTranslationToChat(translatedImageText);
                                  } else {
                                    toast({
                                      title: "Select a Channel First",
                                      description: "Go to Team Chat tab and select or create a channel to send this message",
                                      variant: "destructive"
                                    });
                                  }
                                }}
                              >
                                <MessageSquare className="w-4 h-4 mr-2" />
                                Send to Team Chat
                              </Button>
                            </div>
                            <p className="text-xs text-gray-600 mt-3 text-center">
                              Translation is displayed above. Choose an action to continue.
                            </p>
                          </CardContent>
                        </Card>
                      )}
                    </div>
                  )}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Safety Phrases Tab */}
        <TabsContent value="safety">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center">
                  <Shield className="w-5 h-5 mr-2 text-red-600" />
                  Emergency Safety Phrases
                </CardTitle>
                <CardDescription>
                  Pre-translated phrases for quick emergency communication
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {Object.entries(SAFETY_PHRASES).map(([key, translations]) => (
                    <button
                      key={key}
                      onClick={() => sendSafetyPhrase(key)}
                      className="w-full text-left border rounded-lg p-4 hover:border-red-500 hover:bg-red-50 transition-all"
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <p className="font-semibold text-gray-900 mb-1">
                            {translations[userLanguage]}
                          </p>
                          <p className="text-xs text-gray-500">
                            Click to send to active channel
                          </p>
                        </div>
                        <AlertTriangle className="w-5 h-5 text-red-600 flex-shrink-0 ml-2" />
                      </div>
                    </button>
                  ))}
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="flex items-center">
                  <AlertTriangle className="w-5 h-5 mr-2 text-orange-600" />
                  Emergency Broadcast
                </CardTitle>
                <CardDescription>
                  Send urgent messages to all team members in their language
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label>Emergency Message</Label>
                  <Textarea
                    value={emergencyMessage}
                    onChange={(e) => setEmergencyMessage(e.target.value)}
                    placeholder={`Type your emergency message in ${LANGUAGES.find(l => l.code === userLanguage)?.name}...`}
                    rows={6}
                    className="mt-1"
                  />
                </div>

                <Button
                  onClick={sendEmergencyBroadcast}
                  disabled={loading || !emergencyMessage.trim()}
                  className="w-full bg-red-600 hover:bg-red-700"
                >
                  <AlertTriangle className="w-4 h-4 mr-2" />
                  Send Emergency Broadcast
                </Button>

                <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
                  <p className="text-sm text-orange-900">
                    <strong>Note:</strong> Emergency broadcasts will be sent to all team members
                    immediately and translated to their preferred language. Use only for urgent
                    safety situations.
                  </p>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>
      </Tabs>

      {/* Offline Translation Note */}
      <Card className="bg-blue-50 border-blue-200">
        <CardContent className="p-6">
          <div className="flex items-start gap-4">
            <Download className="w-6 h-6 text-blue-600 flex-shrink-0 mt-1" />
            <div className="flex-1">
              <h3 className="font-semibold text-blue-900 mb-2">Offline Translation Available</h3>
              <p className="text-sm text-blue-800 mb-3">
                Download language packs for offline use. Essential safety phrases will work without internet connection.
              </p>
              <div className="flex gap-2 flex-wrap">
                <Button size="sm" variant="outline" className="border-blue-300 text-blue-700" onClick={downloadAllLanguagePacks}>
                  <Download className="w-4 h-4 mr-2" />
                  Download All Packs
                </Button>
                <Button size="sm" variant="outline" className="border-blue-300 text-blue-700" onClick={checkOfflineStatus}>
                  <Check className="w-4 h-4 mr-2" />
                  Check Offline Status
                </Button>
              </div>
              {offlinePacksInstalled && (
                <div className="mt-3 p-2 bg-green-100 border border-green-300 rounded text-sm text-green-800">
                  ✅ {offlinePacksInstalled} language pack(s) installed for offline use
                </div>
              )}
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default TranslationHub;
